import React from 'react';
import styled from 'styled-components';
import moment from 'utils/moment';
import { monthLabel, rangeDateLabel, daysBetween } from 'TimeOffList/services/format';
import { IconChevronLeft, IconChevronRight, IconCalendar, IconInfo } from 'TimeOffList/components/icons';
import type { PickPhase } from '../services/date-range';

interface Props {
  viewYear: number;
  viewMonth: number;
  rangeStart: string; // 'YYYY-MM-DD', always set
  rangeEnd: string; // 'YYYY-MM-DD', always >= rangeStart
  pickPhase: PickPhase;
  onPickDay: (dayKey: string) => void;
  onChangeFrom: (dayKey: string) => void;
  onChangeTo: (dayKey: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onTomorrow: () => void;
}

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/**
 * Date-range picker. The From/To inputs set either end directly; on the grid a
 * first tap opens the range and a second tap closes it. End === start means the
 * staged record covers a single day.
 */
const DateCalendar: React.FC<Props> = ({
  viewYear, viewMonth, rangeStart, rangeEnd, pickPhase,
  onPickDay, onChangeFrom, onChangeTo, onPrevMonth, onNextMonth, onToday, onTomorrow,
}) => {
  const first = moment({ year: viewYear, month: viewMonth, day: 1 });
  const startDow = (first.day() + 6) % 7;
  const daysInMonth = first.daysInMonth();
  const todayKey = moment().format('YYYY-MM-DD');
  const dayCount = daysBetween(rangeStart, rangeEnd);

  const cells: Array<{ key: string; num: number } | null> = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ key: `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}`, num: d });

  return (
    <Panel>
      <PanelHead>
        <SectionLabel>Date</SectionLabel>
        <div className="quick">
          <Pill onClick={onToday}>Today</Pill>
          <Pill onClick={onTomorrow}>Tomorrow</Pill>
        </div>
      </PanelHead>

      <RangeInputs>
        <Field>
          <span className="lbl">From</span>
          {/* No min/max: picking past the other end drags that end along
              instead of being blocked (see setRangeEdge). */}
          <DateInput type="date" value={rangeStart} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeFrom(e.target.value)} />
        </Field>
        <Arrow>›</Arrow>
        <Field>
          <span className="lbl">To</span>
          <DateInput type="date" value={rangeEnd} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeTo(e.target.value)} />
        </Field>
      </RangeInputs>

      <Card>
        <MonthNav>
          <NavBtn onClick={onPrevMonth} aria-label="Previous month"><IconChevronLeft size={15} strokeWidth={2.4} /></NavBtn>
          <span className="month">{monthLabel(viewYear, viewMonth)}</span>
          <NavBtn onClick={onNextMonth} aria-label="Next month"><IconChevronRight size={15} strokeWidth={2.4} /></NavBtn>
        </MonthNav>
        <DowRow>{DOW.map((d, i) => <span key={i}>{d}</span>)}</DowRow>
        <Grid>
          {cells.map((c, i) => {
            if (!c) return <span key={`b${i}`} />;
            return (
              <DayCell
                key={c.key}
                $edge={c.key === rangeStart || c.key === rangeEnd}
                $inRange={c.key >= rangeStart && c.key <= rangeEnd}
                $today={c.key === todayKey}
                onClick={() => onPickDay(c.key)}
              >
                {c.num}
              </DayCell>
            );
          })}
        </Grid>
      </Card>

      <Banner>
        <IconCalendar size={18} color="var(--acc)" />
        <span className="label">{rangeDateLabel(rangeStart, rangeEnd)}</span>
        <span className="count">{dayCount > 1 ? `${dayCount} days` : '1 day'}</span>
      </Banner>

      <Hint $end={pickPhase === 'end'}>
        <IconInfo size={13} strokeWidth={2.2} />
        <span>
          {pickPhase === 'end'
            ? 'Now tap the end day — or leave it for a single day.'
            : 'Tap a day to start. Tap a second day for a multi-day range.'}
        </span>
      </Hint>
    </Panel>
  );
};

export default DateCalendar;

const Panel = styled.div`
  width: 340px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-1);
  padding: 20px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  .quick { display: flex; gap: 6px; }
`;

const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
`;

const Pill = styled.button`
  padding: 5px 10px;
  border-radius: var(--r-pill);
  border: 1px solid var(--border-1);
  background: var(--bg-surface);
  color: var(--fg-2);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); }
`;

const Card = styled.div`
  border: 1px solid var(--border-1);
  border-radius: var(--r-lg);
  padding: 14px 16px;
  background: var(--bg-surface);
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  .month { font-size: 15px; font-weight: 700; color: var(--fg-1); }
`;

const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: var(--r-sm);
  border: none;
  background: var(--bg-subtle);
  color: var(--fg-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--border-1); }
`;

const DowRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 3px;
  span { text-align: center; font-size: 10px; font-weight: 700; letter-spacing: 0.05em; color: var(--fg-4); padding: 4px 0; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
`;

const DayCell = styled.button<{ $edge: boolean; $inRange: boolean; $today: boolean }>`
  height: 38px;
  border: none;
  border-radius: 9px;
  font-size: 13.5px;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${p => (p.$edge ? 700 : 600)};
  background: ${p => (p.$edge ? 'var(--acc)' : p.$inRange ? 'var(--acc-soft)' : 'transparent')};
  color: ${p => (p.$edge ? '#fff' : p.$inRange ? '#001848' : p.$today ? 'var(--acc)' : '#2C334B')};
  box-shadow: ${p => (!p.$edge && !p.$inRange && p.$today ? 'inset 0 0 0 1.5px var(--acc)' : 'none')};
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--r-lg);
  background: var(--acc-soft50);
  border: 1px solid var(--acc-soft200);
  .label { font-size: 14px; font-weight: 700; color: var(--fg-1); }
  .count { font-size: 12px; color: var(--fg-3); margin-left: auto; white-space: nowrap; }
`;

const Hint = styled.div<{ $end: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${p => (p.$end ? 'var(--acc)' : 'var(--fg-3)')};
  svg { flex-shrink: 0; }
`;

const RangeInputs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const Field = styled.label`
  flex: 1;
  min-width: 0;
  .lbl {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--fg-3);
    margin-bottom: 5px;
  }
`;

const Arrow = styled.span`
  padding-bottom: 11px;
  font-size: 18px;
  line-height: 1;
  color: var(--fg-4);
`;

// Native date input: its value format is already the 'YYYY-MM-DD' key the form
// stores, and it gives the platform's own touch-friendly picker.
const DateInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  border: 1px solid var(--border-1);
  border-radius: var(--r-md);
  background: var(--bg-surface);
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  color: var(--fg-1);
  outline: none;
  &:focus { border-color: var(--border-focus); box-shadow: var(--ring-focus); }
  &::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
`;
